import EditTaskModal from '@/components/features/Projects/modals/EditTaskModal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { useSettings } from '@/contexts/SettingsContext';
import { useIsTabletOrMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { laravelJsonFetch } from '@/lib/laravelJsonFetch';
import { resolveTaskStatusSwatchFromSettings } from '@/lib/projects/ganttTaskStatusStyles';
import { settingLabel } from '@/lib/status-labels';
import { cn } from '@/lib/utils';
import type { ProjectTask } from '@/types';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    pointerWithin,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
    type DragEndEvent,
    type DragStartEvent,
} from '@dnd-kit/core';
import { usePage } from '@inertiajs/react';
import { CalendarDays, User as UserIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

interface KanbanUser {
    id: string;
    name: string;
}

interface Props {
    tasks: ProjectTask[];
    projectOwnerId?: string | null;
    users: KanbanUser[];
}

type PageAuth = {
    auth?: {
        user?: { id?: string | null } | null;
        abilities?: string[];
        roles?: string[];
    };
};

/** Format a `YYYY-MM-DD` date without pulling in a heavier date library. */
function formatDate(value: string | null | undefined): string {
    if (!value) {
        return '';
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
    if (!match) {
        return String(value);
    }
    const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (Number.isNaN(parsed.getTime())) {
        return String(value);
    }
    return parsed.toLocaleDateString();
}

interface TaskCardProps {
    task: ProjectTask;
    swatch: string;
    assigneeName: string;
    draggable: boolean;
    onOpen: () => void;
}

function TaskCard({ task, swatch, assigneeName, draggable, onOpen }: TaskCardProps) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: task.id,
        data: { status: task.status },
        disabled: !draggable,
    });

    const dateRange = [formatDate(task.start_date), formatDate(task.end_date)].filter(Boolean).join(' – ');

    return (
        <Card
            ref={setNodeRef}
            className={cn(
                'cursor-pointer transition-shadow hover:shadow-sm',
                draggable && 'cursor-grab active:cursor-grabbing',
                isDragging && 'opacity-50',
            )}
            onClick={onOpen}
            {...(draggable ? attributes : {})}
            {...(draggable ? listeners : {})}
        >
            <CardContent className="flex flex-col gap-2 p-3">
                <div className="flex items-start gap-2">
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: swatch }} aria-hidden />
                    <p className="min-w-0 flex-1 text-sm font-medium break-words">{task.name}</p>
                </div>
                {assigneeName ? (
                    <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                        <UserIcon className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{assigneeName}</span>
                    </div>
                ) : null}
                {dateRange ? (
                    <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                        <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{dateRange}</span>
                    </div>
                ) : null}
            </CardContent>
        </Card>
    );
}

interface ColumnProps {
    status: { value: string; label: string; color: string };
    swatch: string;
    tasks: ProjectTask[];
    droppable: boolean;
    emptyLabel: string;
    renderCard: (task: ProjectTask) => React.ReactNode;
}

function KanbanColumn({ status, swatch, tasks, droppable, emptyLabel, renderCard }: ColumnProps) {
    const { setNodeRef, isOver } = useDroppable({ id: status.value, disabled: !droppable });

    return (
        <div className="flex w-72 shrink-0 flex-col md:w-full md:min-w-0">
            <div className="mb-2 flex items-center gap-2 px-1">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: swatch }} aria-hidden />
                <span className="text-sm font-semibold">{status.label}</span>
                <Badge variant="secondary" className="ml-auto">
                    {tasks.length}
                </Badge>
            </div>
            <div
                ref={setNodeRef}
                className={cn(
                    'flex min-h-24 flex-1 flex-col gap-2 rounded-xl border border-dashed p-2 transition-colors',
                    isOver ? 'border-primary bg-accent/50' : 'bg-muted/20',
                )}
            >
                {tasks.length === 0 ? (
                    <p className="text-muted-foreground px-2 py-6 text-center text-xs">{emptyLabel}</p>
                ) : (
                    tasks.map((task) => renderCard(task))
                )}
            </div>
        </div>
    );
}

export default function TaskKanbanBoard({ tasks, projectOwnerId, users }: Props) {
    const { t } = useTranslations();
    const { settings } = useSettings();
    const isTabletOrMobile = useIsTabletOrMobile();
    const page = usePage<PageAuth>();

    const currentUserId = page.props.auth?.user?.id ?? null;
    const authAbilities = page.props.auth?.abilities ?? [];
    const authRoles = page.props.auth?.roles ?? [];
    const isProjectOwner = Boolean(currentUserId && projectOwnerId && currentUserId === projectOwnerId);
    const canUpdateAnyTask = authAbilities.includes('tasks.update') || authRoles.includes('Administrator');

    // Drag-to-change-status is a desktop-only affordance; on touch/small screens
    // tapping a card opens the edit modal instead (per the confirmed UX).
    const dragEnabled = canUpdateAnyTask && !isTabletOrMobile;

    const statuses = useMemo(() => settings.projectTaskStatuses ?? [], [settings.projectTaskStatuses]);
    // Column headers use the same translated status labels as the rest of the app.
    const columnStatuses = useMemo(
        () => statuses.map((status) => ({ ...status, label: settingLabel(t, 'project_task_statuses', status.value) })),
        [statuses, t],
    );

    // Real tasks only — the project-timeline pseudo-row is the project bar, not a task.
    const boardTasks = useMemo(() => tasks.filter((task) => !task.is_project_timeline), [tasks]);

    // Local, optimistic copy so a drag can move a card before the server confirms.
    const [localTasks, setLocalTasks] = useState<ProjectTask[]>(boardTasks);
    useEffect(() => {
        setLocalTasks(boardTasks);
    }, [boardTasks]);

    const [selectedTaskForEdit, setSelectedTaskForEdit] = useState<ProjectTask | null>(null);
    const [activeTask, setActiveTask] = useState<ProjectTask | null>(null);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const swatchByStatus = useMemo(() => {
        const map: Record<string, string> = {};
        statuses.forEach((status) => {
            map[status.value] = resolveTaskStatusSwatchFromSettings({ status: status.value, type: 'task' }, statuses);
        });
        return map;
    }, [statuses]);

    const assigneeNameOf = (task: ProjectTask): string => {
        if (task.assignee) {
            return `${task.assignee.first_name ?? ''} ${task.assignee.last_name ?? ''}`.trim();
        }
        if (task.assigned_to) {
            return users.find((user) => user.id === task.assigned_to)?.name ?? '';
        }
        return '';
    };

    const knownStatusValues = useMemo(() => new Set(statuses.map((status) => status.value)), [statuses]);
    const fallbackStatus = statuses[0]?.value ?? '';

    const tasksByStatus = useMemo(() => {
        const grouped: Record<string, ProjectTask[]> = {};
        statuses.forEach((status) => {
            grouped[status.value] = [];
        });
        localTasks.forEach((task) => {
            const bucket = knownStatusValues.has(task.status) ? task.status : fallbackStatus;
            if (!grouped[bucket]) {
                grouped[bucket] = [];
            }
            grouped[bucket].push(task);
        });
        return grouped;
    }, [localTasks, statuses, knownStatusValues, fallbackStatus]);

    const handleDragStart = (event: DragStartEvent) => {
        setActiveTask(localTasks.find((task) => task.id === event.active.id) ?? null);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveTask(null);
        const { active, over } = event;
        if (!over) {
            return;
        }
        const taskId = String(active.id);
        const newStatus = String(over.id);
        const task = localTasks.find((item) => item.id === taskId);
        if (!task || !knownStatusValues.has(newStatus) || task.status === newStatus) {
            return;
        }

        const previousStatus = task.status;
        // Optimistic move.
        setLocalTasks((current) => current.map((item) => (item.id === taskId ? { ...item, status: newStatus as ProjectTask['status'] } : item)));

        void (async () => {
            try {
                await laravelJsonFetch(route('tasks.update', taskId), 'PUT', { status: newStatus });
                toast.success(t('projects.gantt.messages.task_updated', 'Task updated'));
            } catch {
                // Revert on any failure — including a 403 from the backend
                // department-authorization check even though the UI allowed the drag.
                setLocalTasks((current) => current.map((item) => (item.id === taskId ? { ...item, status: previousStatus } : item)));
                toast.error(t('projects.gantt.messages.task_update_failed', 'Failed to update task'));
            }
        })();
    };

    const renderCard = (task: ProjectTask) => (
        <TaskCard
            key={task.id}
            task={task}
            swatch={swatchByStatus[task.status] ?? swatchByStatus[fallbackStatus] ?? ''}
            assigneeName={assigneeNameOf(task)}
            draggable={dragEnabled}
            onOpen={() => setSelectedTaskForEdit(task)}
        />
    );

    const columns = (
        <div className="flex gap-4 overflow-x-auto pb-2 md:grid md:grid-cols-2 md:overflow-visible xl:grid-cols-4">
            {columnStatuses.map((status) => (
                <KanbanColumn
                    key={status.value}
                    status={status}
                    swatch={swatchByStatus[status.value] ?? ''}
                    tasks={tasksByStatus[status.value] ?? []}
                    droppable={dragEnabled}
                    emptyLabel={t('projects.show.kanban.column_empty', 'No tasks')}
                    renderCard={renderCard}
                />
            ))}
        </div>
    );

    return (
        <div className="w-full">
            {boardTasks.length === 0 ? (
                <p className="text-muted-foreground py-12 text-center text-sm">{t('projects.show.kanban.board_empty', 'No tasks yet')}</p>
            ) : dragEnabled ? (
                <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                    {columns}
                    <DragOverlay>
                        {activeTask ? (
                            <TaskCard
                                task={activeTask}
                                swatch={swatchByStatus[activeTask.status] ?? ''}
                                assigneeName={assigneeNameOf(activeTask)}
                                draggable={false}
                                onOpen={() => undefined}
                            />
                        ) : null}
                    </DragOverlay>
                </DndContext>
            ) : (
                columns
            )}

            {selectedTaskForEdit ? (
                <EditTaskModal
                    key={selectedTaskForEdit.id}
                    task={selectedTaskForEdit}
                    users={users}
                    tasks={localTasks.filter((task) => task.id !== selectedTaskForEdit.id)}
                    isProjectOwner={isProjectOwner}
                    isOpen
                    onClose={() => setSelectedTaskForEdit(null)}
                />
            ) : null}
        </div>
    );
}
